import { useRef } from "react";
import type { ReactNode } from "react";
import JoinedTableNames from "../../../reservations/components/seat/JoinedTableNames";
import type { TableCardProps } from "./TableCard";
import { TableCardClosingLabel } from "./TableCardClosingLabel";
import { Highlight, highlightParts } from "./tableCardHighlight";
import { dottedInitialsOf } from "./useBatchWaiterLabels";
import { useTableCardFit } from "./layout/useTableCardFit";
import { CardFactsMeasureLayer, FactLine, FactView } from "./cardInfo/TableCardFactParts";

// Праговете на картата по собствената ѝ ширина (контейнерът `@container/card`
// е клетката в `TableList`). Най-важни са името на масата и сумата — те
// остават винаги, а кегелът им намалява на две стъпала (под 150px и под 112px —
// като картата на телефон), за да се събере и петцифрена сума. Сервитьорът
// реже ли името на масата — само инициалите му (`WaiterName`); под 100px се
// скрива. Фактите за сметката са с кегела на `FactSize` „card".
const FIT = {
  surface: "@max-[112px]/card:rounded-lg @max-[112px]/card:px-2 @max-[112px]/card:py-1.5",
  name: "@max-[150px]/card:text-[17px] @max-[112px]/card:text-sm",
  waiter: "@max-[100px]/card:hidden",
  total: "@max-[150px]/card:text-base @max-[112px]/card:text-[13px]",
};

// Кегелите; на името и сумата — общи за видимото и за слоя за мерене
// (`CardFactsMeasureLayer`).
const NAME_FONT = "font-bold leading-[1.2] text-[21px]";
const WAITER_FONT = "text-[12px] leading-[1.2]";
const TOTAL_FONT = "font-pos-mono text-[19px] font-semibold leading-none";

// Сервитьорът в ниската карта: цял, или съкратен с „…", но само ако има поне
// 60px място — иначе инициалите (`data-waiter`, `useTableCardFit`), а не се
// ли събират и те — изчезва изцяло (без огризки като „Т…": инициалите не се
// свиват, `min-w-max`). Трикът: редът е flex-wrap с височина един ред и
// overflow-hidden; нулевият разделител пред името позволява то да се пренесе
// на втория (невидим) ред, щом не се събира. Не мести сумата (basis 0).
function NameIfRoom({ title, className, children }: { title: string; className: string; children: ReactNode }) {
  return (
    <span className={`flex h-[1.2em] min-w-0 flex-[1_1_0%] flex-wrap overflow-hidden leading-[1.2] ${className}`}>
      <span aria-hidden className="h-[1.2em] w-0" />
      <span
        data-waiter-room
        title={title}
        className="min-w-[60px] max-w-full truncate group-data-[waiter=initials]/tablecard:min-w-max"
      >
        {children}
      </span>
    </span>
  );
}

// Името на сервитьора или — щом пълното реже името на масата (`data-waiter`,
// `useTableCardFit`) — инициалите му („Т.П.", като до часа на поръчката).
function WaiterName({ label, searchTerm }: { label: string; searchTerm: string }) {
  return (
    <>
      <span className="group-data-[waiter=initials]/tablecard:hidden">
        {highlightParts(label, searchTerm)}
      </span>
      <span className="hidden group-data-[waiter=initials]/tablecard:inline">
        {dottedInitialsOf(label)}
      </span>
    </>
  );
}

interface DesktopTableCardProps extends Omit<TableCardProps, "compact"> {
  /** Ниска карта (`CARD_ONE_LINE_BELOW`): името и сумата на един ред. */
  oneLine: boolean;
}

/**
 * Картата на една маса в списъка на „Маркиране" на десктоп (`TableList`; на
 * телефон/таблет — `TableCard`). Размерът е от дръжките (`CardResizeGrips`) и
 * е еднакъв за всички карти — съдържанието се събира в него: дългото име на
 * масата и на сервитьора се пренасят на толкова редове, колкото има място, и
 * се режат с „…" чак след това. Котвите не мърдат: името горе вляво,
 * сервитьорът (на чужда маса) горе вдясно, сумата долу вдясно. Фактите за
 * сметката (резервация, гости, „Карта — без бон", депозит, клиент, отстъпки)
 * растат от сумата нагоре: всеки на свой ред, докато има височина, а които не
 * стигат — на един ред до сумата, всички в един вид (текст, стойности или
 * иконки) — `planCardFacts` (`useTableCardFit`). До името — нищо. Маса без
 * факти — както без тях. „Закрива се" — само името и етикетът.
 */
export function DesktopTableCard({
  cardRef,
  name,
  tableNames,
  total,
  waiterLabel,
  hasClient,
  isSelected,
  isClosedEmpty,
  isClosing = false,
  facts,
  searchTerm,
  oneLine,
  onClick,
  onDoubleClick,
}: DesktopTableCardProps) {
  const cardElementRef = useRef<HTMLButtonElement | null>(null);
  // Всичко, от което зависят мерките в картата (`useTableCardFit`).
  const plan = useTableCardFit(
    cardElementRef,
    oneLine ? "oneLine" : "twoRow",
    JSON.stringify([
      name,
      tableNames,
      total,
      waiterLabel,
      isClosing,
      facts,
      searchTerm,
    ]),
    facts,
  );

  // Цветовете — като в `TableCard`: избраната маса с маркиран фон и акцентна
  // рамка, закритата празна и тази в процес на закриване — приглушени.
  const surface = isClosing
    ? "bg-pos-closed-bg border-pos-closed-brd"
    : isSelected
      ? "bg-pos-marked border-pos-accent"
      : isClosedEmpty
        ? "bg-pos-closed-bg border-pos-closed-brd"
        : "bg-pos-card border-pos-line";
  // Име с клиент носи акцентен цвят — сигнал, че има закачен клиент.
  const nameColor = isClosedEmpty
    ? "text-pos-dim"
    : hasClient
      ? "text-pos-accent-ink"
      : "text-pos-ink";
  const joinedNames = tableNames && tableNames.length > 1 ? tableNames : null;
  const nameContent = joinedNames ? (
    <JoinedTableNames
      names={joinedNames}
      renderName={(tableName) => highlightParts(tableName, searchTerm)}
    />
  ) : (
    highlightParts(name, searchTerm)
  );

  const hasFacts = facts.length > 0;
  // Редът с факти — до сумата (в ниската карта — между името и сумата) или,
  // щом до нея не стига място, точно над нея. -mb-px — оптично: долният ръб е
  // на сумата (items-end), а базовата линия на 12px текст стои ~1px над тази
  // на сумата (items-baseline е по-зле — иконката сяда на базовата линия).
  const factLine =
    plan.line.length > 0 ? (
      <FactLine
        items={plan.line}
        form={plan.lineForm}
        facts={facts}
        hidden={plan.hidden}
        size="card"
        searchTerm={searchTerm}
        className={oneLine ? "order-1" : plan.lineAboveSum ? "" : "-mb-px"}
      />
    ) : null;
  const lineFacts = plan.lineAboveSum ? null : factLine;
  // Фактите в реда на ниската карта: името не отстъпва (планът е оставил
  // място) — при мерене е както без тях.
  const nameFlex = oneLine
    ? lineFacts
      ? "flex-none group-data-[measuring]/tablecard:flex-[0_1_auto]"
      : "flex-[0_1_auto]"
    : "flex-1";
  const hasUpperLines = !oneLine && (plan.rows.length > 0 || plan.lineAboveSum);

  // Долен ред (в ниската карта — продължение на горния): вляво фактите,
  // вдясно сумата — винаги.
  const lastLine = (
    <div className={oneLine ? "contents" : "flex w-full min-w-0 shrink-0 items-end gap-2"}>
      {isClosing ? (
        <TableCardClosingLabel compact={false} />
      ) : (
        <>
          {lineFacts}
          <Highlight
            text={total.toFixed(2)}
            term={searchTerm}
            className={`ml-auto shrink-0 ${TOTAL_FONT} ${
              isClosedEmpty ? "text-pos-dim" : "text-pos-ink"
            } ${oneLine ? "order-3" : ""} ${FIT.total}`}
          />
        </>
      )}
    </div>
  );

  return (
    <button
      ref={(element) => {
        cardElementRef.current = element;
        cardRef(element);
      }}
      type="button"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      disabled={isClosing}
      // Висока карта: горният ред (името) взима мястото над долния (сумата);
      // ниска (`oneLine`): всичко в един ред — името, фактите, сервитьорът и
      // сумата (`order-*`). `group/tablecard` — `data-waiter` и
      // `data-measuring` (`useTableCardFit`), `data-selected` — за „+N".
      // Само с факти: `relative` — слоят за мерене; ниската карта под 112px
      // — по-малко отстояние, за да стигне място за „+N" (`planOneLine`).
      data-selected={hasFacts && isSelected ? "" : undefined}
      className={`group/tablecard flex min-w-0 flex-1 gap-2 overflow-hidden text-left border-[1.5px] rounded-[14px] p-[8px_14px] ${surface} ${
        isClosing ? "cursor-not-allowed" : "cursor-pointer"
      } ${oneLine ? "items-center" : "flex-col gap-y-1.5"} ${FIT.surface}${
        hasFacts ? ` relative${oneLine ? " @max-[112px]/card:gap-x-1.5" : ""}` : ""
      }`}
    >
      {/* Горен ред: името на масата + (на чужда маса) сервитьорът, воден по
          ГОРНИЯ ръб на името (mt компенсира разликата в half-leading + cap
          height на двата кегела). В ниската карта — `contents`: частите са в
          общия ред; името там е на един ред с „…". */}
      <div
        data-card-top
        className={oneLine ? "contents" : "flex min-h-0 w-full min-w-0 flex-1 items-start gap-2 overflow-hidden"}
      >
        <span
          data-card-name
          className={`min-w-0 [overflow-wrap:anywhere] ${NAME_FONT} ${nameColor} ${FIT.name} ${
            oneLine
              ? `${nameFlex} truncate`
              : `${nameFlex} line-clamp-[var(--name-lines,1)] data-[clamp=single]:block data-[clamp=single]:truncate`
          }`}
        >
          {nameContent}
        </span>
        {/* При закриване картата показва само името и „Закрива се". Името
            на масата е с предимство: реже ли го пълното име на сервитьора —
            инициалите (`WaiterName`). В ниската карта сервитьорът е до сумата,
            само ако остане място след името и фактите (`NameIfRoom`) — иначе
            изчезва, без огризка. */}
        {waiterLabel && !isClosing &&
          (oneLine ? (
            <NameIfRoom title={waiterLabel} className={`order-2 justify-end text-[12px] text-pos-muted ${FIT.waiter}`}>
              <WaiterName label={waiterLabel} searchTerm={searchTerm} />
            </NameIfRoom>
          ) : (
            <span
              data-card-waiter
              // На един ред — до 55% от ширината („Тест Потреби…"), като досега;
              // на няколко — до 40%, за да остане на името място за цели думи,
              // но поне колкото най-дългата дума (min-w-min) — без „Потребит/ел".
              title={waiterLabel}
              className={`mt-[3px] min-w-min max-w-[40%] shrink-0 text-right ${WAITER_FONT} text-pos-muted [overflow-wrap:break-word] line-clamp-[var(--waiter-lines,1)] data-[clamp=single]:block data-[clamp=single]:min-w-0 data-[clamp=single]:max-w-[55%] data-[clamp=single]:truncate ${FIT.waiter}`}
            >
              <WaiterName label={waiterLabel} searchTerm={searchTerm} />
            </span>
          ))}
      </div>

      {/* Над сумата — фактите на свой ред и (ако до сумата не стига място)
          редът с факти; растат нагоре от нея, само ако ги има. */}
      {hasUpperLines ? (
        <div className="flex w-full min-w-0 shrink-0 flex-col gap-y-1">
          {plan.rows.map(({ index, form }) =>
            facts[index] ? (
              <FactView key={index} fact={facts[index]} form={form} size="card" searchTerm={searchTerm} />
            ) : null,
          )}
          {plan.lineAboveSum && factLine}
          {lastLine}
        </div>
      ) : (
        lastLine
      )}

      {hasFacts && (
        <CardFactsMeasureLayer
          facts={facts}
          size="card"
          searchTerm={searchTerm}
          nameClassName={`${NAME_FONT} ${FIT.name}`}
          name={nameContent}
          sumClassName={`${TOTAL_FONT} ${FIT.total}`}
          total={total.toFixed(2)}
        />
      )}
    </button>
  );
}
